/**
 * Avisos al móvil (Web Push) en este dispositivo.
 *
 * Se activan por dispositivo: el permiso es del navegador, no de la cuenta.
 * El servidor guarda la suscripción con el idioma del dispositivo para
 * escribir los avisos en ese idioma.
 */

import { useEffect, useSyncExternalStore } from 'react';

import { api } from './api';
import { getLanguage } from './i18n';

export type PushState =
  /** El navegador no los admite (o es desarrollo, sin service worker). */
  | 'unsupported'
  /** iPhone/iPad en Safari: primero hay que añadir la app a la pantalla de inicio. */
  | 'needs-install'
  /** La persona los bloqueó; solo se cambia desde el navegador. */
  | 'denied'
  | 'off'
  | 'on';

let state: PushState = 'off';
const listeners = new Set<() => void>();
const set = (next: PushState) => {
  state = next;
  listeners.forEach((listener) => listener());
};

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
}

function supported(): boolean {
  return import.meta.env.PROD && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  return (await registration()).pushManager.getSubscription();
}

async function save(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api.post('/notifications/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    lang: getLanguage(),
  });
}

/** Mira cómo está este dispositivo. */
export async function refreshPushState(): Promise<void> {
  if (!supported()) {
    set(isIos() && !isStandalone() ? 'needs-install' : 'unsupported');
    return;
  }
  if (Notification.permission === 'denied') {
    set('denied');
    return;
  }
  const subscription = await currentSubscription().catch(() => null);
  set(subscription && Notification.permission === 'granted' ? 'on' : 'off');
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

/** Pide permiso y se suscribe. Devuelve el estado final. */
export async function enablePush(): Promise<PushState> {
  if (!supported()) {
    await refreshPushState();
    return state;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    set(permission === 'denied' ? 'denied' : 'off');
    return state;
  }

  const { key } = await api.get<{ key: string }>('/notifications/push/key');
  const reg = await registration();
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }
  await save(subscription);
  set('on');
  return state;
}

/** Deja de recibir avisos en este dispositivo. */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription().catch(() => null);
  if (subscription) {
    await api.post('/notifications/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => undefined);
    await subscription.unsubscribe().catch(() => false);
  }
  await refreshPushState();
}

/**
 * Al cerrar sesión: el dispositivo deja de recibir avisos de esa cuenta,
 * pero se conserva la suscripción del navegador para quien entre después.
 */
export async function forgetPushForSession(): Promise<void> {
  const subscription = await currentSubscription().catch(() => null);
  if (subscription) {
    await api.post('/notifications/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => undefined);
  }
}

export function usePushState(): PushState {
  useEffect(() => {
    void refreshPushState();
  }, []);
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

/**
 * Con la sesión iniciada, vuelve a mandar la suscripción de este dispositivo:
 * así queda ligada a la cuenta que ha entrado y con el idioma actual.
 */
export function usePushSync(userId: string | null): void {
  useEffect(() => {
    if (!userId || !supported() || Notification.permission !== 'granted') return;
    void currentSubscription()
      .then((subscription) => (subscription ? save(subscription) : undefined))
      .catch(() => undefined);
  }, [userId]);
}
